import {
  type IntakeQuestionChoice,
  formatPreviousResponse,
  responseIsPrefilled,
} from "@/app/directories/checkout/components/intake/IntakeForm";
import type { fieldSchema } from "@/app/directories/checkout/components/intake/IntakeFormTypes";
import type { GetConsumerByConsumerIdQueryConsumer as Consumer } from "@frontend/lib/graphql/gql-types";
import type { GetUserByUserIdQueryUser as User } from "@frontend/lib/graphql/gql-types";
import type { GetIntakeFormsAndQuestionsByFormIdsQueryFormToQuestion as FormToQuestion } from "@frontend/lib/graphql/gql-types";
import type { z } from "zod";

import {
  type IntakeData,
  translateIntakeForm,
} from "@/app/directories/companion/components_v2/AppointmentForm/hooks/useIntakeForm";
import { datadogLogs } from "@datadog/browser-logs";
import {
  formatPhoneDisplay,
  formatPhoneInput,
} from "@frontend/components/input/PhoneStorefrontInput";
import type {
  IntakeFormData,
  IntakeFormRichResponse,
  IntakeFormSavedData,
  PolymorphicSavedResponse,
} from "@frontend/components/intake/Intake";
import {
  getIntakeFormResponse,
  getIntakeFormsByFormIds,
  getRetrievableIntakeResponsesByIds,
  getSuperscriptQuestions,
  writeIntakeFormToEhr,
} from "@frontend/lib/actions/actions";
import { getIntakeFormsAndQuestionsByFormIds } from "@frontend/lib/actions/actions";
import {
  AppointmentStatusEnum,
  LanguageCode,
} from "@frontend/lib/graphql/generated-gql";
import type { ResponseDto } from "@frontend/lib/interfaces";
import type { useTranslations } from "@frontend/lib/next-intl-typed";
import { chunkArray, tryCatchRethrow } from "@frontend/shared/lib/utils";
import imageCompression from "browser-image-compression";
import { differenceInCalendarDays, parseISO } from "date-fns";
import type { ClinicalProviderFormData } from "./IntakeClinicalProviderInput";
import type { IntakeListEntry } from "./IntakeListInput";
import { getConsumerPhotosToPrompt } from "./consumerPhotosHelper";

export type IntakeStepTypes =
  | "loading"
  | "review"
  | "photos"
  | "questions"
  | "success"
  | "redirecting";

export type TypeformIntakeFormSection = {
  sectionId: string;
  formId: number;
  sectionText: string;
  sectionName: string;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  questions: any[];
  priority: number;
  sectionTranslations: {
    languageCode: string;
    text: string;
    name: string;
  }[];
};

export type TypeformIntakeFormSectionWithResponses =
  TypeformIntakeFormSection & {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    responses: any[];
  };

export type IntakeFormResult = {
  intakeData: IntakeData[];
  areAllIntakeFormsCompleted: boolean;
  intakeFormSectionDetails?: {
    sectionId: string;
    formId: string;
    sectionName: string;
    sectionText: string;
    priority: number;
    sectionTranslations: {
      languageCode: string;
      text: string;
      name: string;
    }[];
  }[];
  isLoading?: boolean;
};

const superscriptQuestionIdToOptionsEndpointMap = (
  superscriptQuestionId: number,
) => {
  switch (superscriptQuestionId) {
    case 22:
      return "/api/intake/search-athena-medications";
    case 23:
      return "/api/intake/search-athena-pharmacy";
    case 24:
      return "/api/intake/search-athena-provider";
    // no case 25 because its direct pharmacy search
    case 26:
      return "/api/intake/search-experity-pcp";
    default:
      return undefined;
  }
};

/**
 * Server-compatible version of the intake form functionality
 * Can be used in server components with async/await
 */
export async function getIntakeFormDataForServer(
  intakeFormIds: number[],
  appointmentId: string,
  locale?: LanguageCode,
): Promise<IntakeFormResult> {
  if (!intakeFormIds?.length) {
    return {
      intakeData: [],
      areAllIntakeFormsCompleted: false,
      intakeFormSectionDetails: [],
    };
  }

  // Fetch intake forms and responses in parallel
  const [rawIntakeForms, rawIntakeResponse] = await Promise.all([
    getIntakeFormsAndQuestionsByFormIds(intakeFormIds),
    getIntakeFormResponse(appointmentId, intakeFormIds),
  ]);

  // Process the intake forms
  const processedIntakeForms = rawIntakeForms.map((form) => ({
    ...form,
    intakeFormToQuestions: translateIntakeForm(
      form.intakeFormToQuestions,
      locale ?? LanguageCode.En,
    )
      .map((question) => ({
        ...question,
        intakeQuestion: {
          ...question.intakeQuestion,
          questionAttributes: {
            ...question.intakeQuestion.questionAttributes,
            choices: question.intakeQuestion.questionAttributes.choices?.map(
              (c, index) => ({
                ...c,
                id: index.toString(),
              }),
            ),
          },
        },
      }))
      .sort((a, b) => a.questionNumber - b.questionNumber),
  }));

  // Extract and organize intakeFormSections from the rawIntakeForms
  const intakeFormSections = rawIntakeForms.flatMap((form) =>
    form.intakeFormToQuestions.map((question) => {
      const section = question.intakeQuestion.intakeFormSection;
      return {
        sectionId: section?.id?.toString() ?? "",
        formId: form.id.toString(),
        sectionName: section?.name ?? "",
        sectionText: section?.text ?? "",
        priority: section?.priority ?? 0,
        sectionTranslations: section?.intakeFormSectionTranslations ?? [],
      };
    }),
  );

  // Map forms to their responses
  const intakeData: IntakeData[] = processedIntakeForms.map((form) => ({
    form,
    response: rawIntakeResponse.find((x) => x.intakeFormId === form.id),
    isComplete: rawIntakeResponse.find((x) => x.intakeFormId === form.id)
      ?.isComplete,
  }));

  // Check if all forms are completed
  const areAllIntakeFormsCompleted = intakeData.every(
    (data) => !!data.response?.isComplete,
  );

  return {
    intakeData,
    areAllIntakeFormsCompleted,
    intakeFormSectionDetails: intakeFormSections,
  };
}

export const intakeSetup = async (
  formIds: number[],
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  appointment: any,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  user: any,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  consumer: any,
  appointmentId: string,
  orgId: number,
  isWaitingRoom: boolean,
) => {
  const firstSlot = appointment.detailSlots[0];

  const isAppointmentActive =
    firstSlot.appointment.status === AppointmentStatusEnum.Active;

  const prefillArray =
    formIds.length > 0
      ? await getIntakeFormResponse(appointmentId, formIds)
      : [];

  const intakeFormsData = (await getIntakeFormsByFormIds(
    formIds,
  )) as IntakeFormData[];
  const prefillInfo = new Map(
    prefillArray
      .filter((prefill) => !!prefill.responses)
      .map((x) => [x.intakeFormId, x]),
  );

  const allIntakeData = await getIntakeFormDataForServer(
    formIds,
    appointmentId,
    user.primaryLanguage,
  );

  const { intakeData, areAllIntakeFormsCompleted, intakeFormSectionDetails } =
    allIntakeData;

  const superscriptQuestions = await getSuperscriptQuestions(orgId);

  const allQuestions = intakeData.flatMap((form) =>
    form.form.intakeFormToQuestions.flatMap((question) => ({
      ...question,
      formId: form.form.id,
      isComplete: form.isComplete,
    })),
  );
  for (const ssQuestion of superscriptQuestions) {
    if (ssQuestion.type === "LIST") {
      const listOptionsEndpoint = superscriptQuestionIdToOptionsEndpointMap(
        ssQuestion.id,
      );
      for (const subQuestion of ssQuestion.intakeQuestions) {
        const question = allQuestions.find(
          (q) => q.intakeQuestion.id === subQuestion.id,
        );
        if (question) {
          question.intakeQuestion.questionAttributes.listOptionsEndpoint =
            listOptionsEndpoint;
        }
      }
    }

    if (ssQuestion.type === "CLINICAL_PROVIDER") {
      const clinicalProviderOptionsEndpoint =
        superscriptQuestionIdToOptionsEndpointMap(ssQuestion.id);
      for (const subQuestion of ssQuestion.intakeQuestions) {
        const question = allQuestions.find(
          (q) => q.intakeQuestion.id === subQuestion.id,
        );
        if (question) {
          question.intakeQuestion.questionAttributes.clinicalProviderOptionsEndpoint =
            clinicalProviderOptionsEndpoint;
          if (ssQuestion.id === 23) {
            question.intakeQuestion.questionAttributes.searchMode = "auto";
            question.intakeQuestion.questionAttributes.mode = "pharmacy";
          } else if (ssQuestion.id === 25) {
            question.intakeQuestion.questionAttributes.searchMode =
              "button-search";
            question.intakeQuestion.questionAttributes.mode = "directPharmacy";
          } else {
            question.intakeQuestion.questionAttributes.searchMode = "auto";
            question.intakeQuestion.questionAttributes.mode = "pcp";
          }
        }
      }
    }
  }

  const deduplicatedQuestions = allQuestions.filter(
    (question, index, self) =>
      index ===
      self.findIndex((t) => t.intakeQuestion.id === question.intakeQuestion.id),
  );

  const isFormAccessible = (formId: number) => {
    const form = intakeFormsData.find((form) => form.id === formId);
    if (
      form?.completionTiming === "DAY_OF_SERVICE" &&
      differenceInCalendarDays(firstSlot.startTime, new Date()) > 0
    ) {
      return false;
    }

    // nick - 2/2/26 - removed restriction on appt being active, still check if isWaitingRoom
    if (form?.completionTiming === "IN_OFFICE") {
      if (isWaitingRoom) {
        return true;
      }

      return false;
    }

    return !isFormComplete(formId, intakeData);
  };
  const allAccessibleQuestions = deduplicatedQuestions.filter((question) =>
    isFormAccessible(question.formId),
  );

  const addressQuestion = allAccessibleQuestions.find(
    (question) => question.intakeQuestion.superscriptIntakeQuestion?.id === 17,
  );
  const addressQuestionId = addressQuestion?.intakeQuestion.id;

  for (const question of allAccessibleQuestions) {
    if (question.intakeQuestion.type === "CLINICAL_PROVIDER") {
      question.intakeQuestion.questionAttributes.addressQuestionId =
        addressQuestionId;
    }
  }

  const totalQuestionsCount = allAccessibleQuestions.length;

  const questionIds = deduplicatedQuestions.map(
    (question) => question.intakeQuestion.id,
  );

  const retrievableIntakeResponses = await getRetrievableIntakeResponsesByIds(
    questionIds,
    consumer.id,
  );

  const {
    pcUpdatedRetrievableIntakeResponses,
    updatedPrefillInfo,
    hasStartedIntake,
  } = await updateRetrievableResponsesWithPC(
    consumer,
    retrievableIntakeResponses,
    user,
    allAccessibleQuestions,
    orgId,
    prefillInfo,
    appointmentId,
    appointment.detailSlots[0]?.createdAt,
    intakeFormsData,
    superscriptQuestions,
  );

  const allQuestionsBySectionId = deduplicatedQuestions.reduce<
    Record<string, typeof deduplicatedQuestions>
  >((acc, question) => {
    const sectionId =
      question.intakeQuestion.intakeFormSection?.id?.toString() || "unknown";
    if (sectionId === "unknown") {
      console.warn("unsorted questions!!", question.intakeQuestion.id);
      //skip this question, shouldn't happen tho
      return acc;
    }
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(question);
    return acc;
  }, {});

  const allQuestionsByFormSection = Object.entries(allQuestionsBySectionId)
    .map(([sectionId, questions]) => {
      const sectionDetails = intakeFormSectionDetails?.find(
        (section) => section.sectionId?.toString() === sectionId,
      );
      return {
        sectionId,
        questions,
        formId: Number(sectionDetails?.formId),
        sectionName: sectionDetails?.sectionName || "",
        sectionText: sectionDetails?.sectionText || "",
        priority: Number(sectionDetails?.priority) || 0,
        sectionTranslations: sectionDetails?.sectionTranslations || [],
      } as TypeformIntakeFormSection;
    })
    .sort((a, b) => a.priority - b.priority);

  const availableQuestionsBySectionId = allAccessibleQuestions.reduce<
    Record<string, typeof deduplicatedQuestions>
  >((acc, question) => {
    const sectionId =
      question.intakeQuestion.intakeFormSection?.id?.toString() || "unknown";
    if (sectionId === "unknown") {
      console.warn("unsorted questions!!", question.intakeQuestion.id);
      //skip this question, shouldn't happen tho
      return acc;
    }
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(question);
    return acc;
  }, {});

  const availableQuestionsByFormSection = Object.entries(
    availableQuestionsBySectionId,
  )
    .map(([sectionId, questions]) => {
      const sectionDetails = intakeFormSectionDetails?.find(
        (section) => section.sectionId?.toString() === sectionId,
      );
      return {
        sectionId,
        questions,
        formId: Number(sectionDetails?.formId),
        sectionName: sectionDetails?.sectionName || "",
        sectionText: sectionDetails?.sectionText || "",
        priority: Number(sectionDetails?.priority) || 0,
        sectionTranslations: sectionDetails?.sectionTranslations || [],
      } as TypeformIntakeFormSection;
    })
    .sort((a, b) => a.priority - b.priority);

  // Group responses by section ID
  const responsesBySectionId = pcUpdatedRetrievableIntakeResponses.reduce<
    Record<string, typeof pcUpdatedRetrievableIntakeResponses>
  >((acc, response) => {
    const sectionId =
      response.intakeQuestion.intakeFormSection?.id?.toString() || "unknown";
    if (sectionId === "unknown") {
      //skip this question, shouldn't happen tho
      console.warn("unsorted questions!!", response.intakeQuestion.id);
      return acc;
    }
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(response);
    return acc;
  }, {});

  // Create the section-organized structure using intakeFormSectionDetails
  const pcUpdatedRetrievableByFormSection = Object.entries(responsesBySectionId)
    .map(([sectionId, responses]) => {
      // Find the matching section details
      const sectionDetails = intakeFormSectionDetails?.find(
        (section) => section.sectionId?.toString() === sectionId,
      );

      // Map responses to questions format needed by TypeformIntakeFormSection
      const questions = responses.map((response) => ({
        ...response.intakeQuestion,
        id: response.intakeQuestion.id,
        questionText: response.intakeQuestion.questionText,
        type: response.intakeQuestion.type,
        questionAttributes: response.intakeQuestion.questionAttributes,
        questionNumber: response.intakeQuestion.questionNumber,
        section: sectionId,
      }));

      if (
        availableQuestionsByFormSection.find(
          (section) => section.sectionId === sectionId,
        )?.questions.length !== questions.length
      ) {
        // check if any of the unanswered questions are required
        const sectionQuestions = availableQuestionsByFormSection.find(
          (section) => section.sectionId === sectionId,
        )?.questions;

        const questionsMissingResponses = sectionQuestions?.filter(
          (question) =>
            !questions.some(
              (response) => response.id === question.intakeQuestion.id,
            ),
        );
        const requiredQuestions =
          questionsMissingResponses?.filter(
            (question) => question.isRequired,
          ) || [];
        if (requiredQuestions.length > 0) {
          return null;
        }
      }

      return {
        sectionId,
        formId: Number(sectionDetails?.formId),
        sectionName: sectionDetails?.sectionName || "",
        sectionText: sectionDetails?.sectionText || "",
        priority: Number(sectionDetails?.priority) || 0,
        sectionTranslations: sectionDetails?.sectionTranslations || [],
        responses,
        questions,
      } as TypeformIntakeFormSectionWithResponses;
    })
    .filter((section) => section !== null) // sections with required questions that are unanswered will be filtered out to be completed in the stepper
    .sort((a, b) => a.priority - b.priority);

  const formSectionsToComplete = availableQuestionsByFormSection.filter(
    (section) =>
      !pcUpdatedRetrievableByFormSection.some(
        (q) => q.sectionId === section.sectionId,
      ),
  );

  const anyFormsUnavailable = formIds
    .map((id) => isFormAccessible(id) || isFormComplete(id, intakeData))
    .some((x) => !x);

  const consumerPhotosToPrompt = await getConsumerPhotosToPrompt(
    consumer.id,
    orgId,
    consumer.insurances,
  );

  return {
    prefillInfo: updatedPrefillInfo,
    intakeData,
    intakeFormsData,
    pcUpdatedRetrievableByFormSection,
    pcUpdatedRetrievableIntakeResponses,
    allQuestionsByFormSection,
    availableQuestionsByFormSection,
    totalQuestionsCount,
    formSectionsToComplete,
    areAllIntakeFormsCompleted,
    anyFormsUnavailable,
    hasStartedIntake,
    consumerPhotosToPrompt,
    orgId,
  };
};

const superscriptquestionIdToConsumerUserFieldMap = new Map<
  number,
  keyof Consumer | keyof User | string
>([
  [18, "sex"],
  [2, "race"],
  [3, "ethnicity"],
  [4, "maritalStatus"],
  [1, "primaryLanguage"],
  [8, "emergencyContactFirstName"],
  [8, "emergencyContactLastName"],
  [11, "emergencyContactPhone"],
  [19, "emergencyContactRelationship"],
  [9, "pharmacyName"],
  [10, "pharmacyPhone"],
  [7, "pcpFirstName"],
  [7, "pcpLastName"],
  [14, "pcpPhone"],
  [13, "email"],
  [12, "phone"],
  [6, "streetAddress"],
  [15, "city"],
  [16, "state"],
  [17, "zip"],
  [23, "clinicalPharmacySearch"],
  [24, "clinicalPcpSearch"],
  [25, "pharmacyDirectSearch"],
]);

// gets first instance of 5 digits from the end of the string
// wouldn't have to do this if we had a zip field on the practice model
// but we don't so
export const getZipFromAddressString = (address: string) => {
  const zip = address.split("").reverse().join("").match(/\d{5}/);
  if (zip) {
    return zip[0].split("").reverse().join("");
  }
  return null;
};

export const compareLanguageCodeToUserLocale = (
  languageCode: string,
  userLocale: LanguageCode,
) => {
  if (languageCode === userLocale) {
    return true;
  }
  if (languageCode.replace("-", "_") === userLocale) {
    return true;
  }
  return false;
};

const formatResponseForCopy = (
  question: z.infer<typeof fieldSchema>,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  response: IntakeFormRichResponse | string | string[] | undefined | any,
) => {
  if (response === undefined) {
    return "";
  }
  switch (question.type) {
    case "SIGNATURE":
      return (response as IntakeFormRichResponse).isSignatureComplete
        ? "Signed"
        : "Not Signed";
    case "MULTI_SELECT": {
      const choices = (
        question.questionAttributes as { choices: IntakeQuestionChoice[] }
      ).choices;
      return choices
        .filter((c) => (response as string[]).includes(c.id))
        .map((c) => c.value)
        .join(", ");
    }
    case "SINGLE_SELECT": {
      const choices = (
        question.questionAttributes as { choices: IntakeQuestionChoice[] }
      ).choices;

      return (
        choices.find((c) => response.toString().includes(c.id))?.value ?? ""
      );
    }
    case "LIST": {
      if (typeof response === "string" && response.includes("[")) {
        try {
          const json = JSON.parse(response);
          return (json as IntakeListEntry[])
            .map((item) => item.value)
            .join(", ");
        } catch (error) {
          console.warn(
            "Error parsing list response inside intakeHelper.ts",
            error,
          );
          return response as string;
        }
      }
      if (Array.isArray(response)) {
        return response.map((item) => item.value).join(", ");
      }
      return response as string;
    }
    case "CLINICAL_PROVIDER": {
      if (typeof response === "string") {
        try {
          const parsedValue = JSON.parse(response) as ClinicalProviderFormData;
          if (parsedValue.name === "" && parsedValue.phone === "") {
            return "N/A";
          }
          if (parsedValue.phone) {
            parsedValue.phone = formatPhoneDisplay(
              formatPhoneInput(parsedValue.phone),
            );
          }
          return `${parsedValue.name}, ${parsedValue.address.streetAddress}, ${parsedValue.address.city}, ${parsedValue.address.state}, ${parsedValue.address.zip}, ${parsedValue.phone}`;
        } catch (error) {
          console.warn(
            "Error parsing clinical provider response inside intakeHelper.ts",
            error,
          );
          return "N/A";
        }
      }
      if (typeof response === "object") {
        if (response.name === "" && response.phone === "") {
          return "N/A";
        }
        if (response.phone) {
          response.phone = formatPhoneDisplay(formatPhoneInput(response.phone));
        }

        return `${response.name}, ${response.address.streetAddress}, ${response.address.city}, ${response.address.state}, ${response.address.zip}, ${response.phone}`;
      }
      return response;
    }
    case "PHONE_NUMBER": {
      return response as string; // can't format phone number here bc does not run on server
    }
    default:
      return response as string;
  }
};

const languageCodeToAnswer = {
  en: {
    fullText: "English",
    id: 0,
  },
  es: {
    fullText: "Spanish",
    id: 1,
  },
  ru: {
    fullText: "Russian",
    id: 2,
  },
  zh: {
    fullText: "Chinese (Simplified)",
    id: 3,
  },
  zh_HK: {
    fullText: "Chinese (Traditional)",
    id: 4,
  },
};

const ethnicityToAnswer = {
  HISPANIC_OR_LATINO: {
    fullText: "Hispanic or Latino",
    id: 0,
  },
  NOT_HISPANIC_OR_LATINO: {
    fullText: "Non Hispanic or Latino",
    id: 1,
  },
  DECLINE_TO_ANSWER: {
    fullText: "Decline to Answer",
    id: 2,
  },
};

const raceToAnswer = {
  AMERICAN_INDIAN_OR_ALASKA_NATIVE: {
    fullText: "American Indian or Alaska Native",
    id: 0,
  },
  ASIAN: {
    fullText: "Asian",
    id: 1,
  },
  BLACK_OR_AFRICAN_AMERICAN: {
    fullText: "Black or African American",
    id: 2,
  },
  MIDDLE_EASTERN_OR_NORTH_AFRICAN: {
    fullText: "Middle Eastern or North African",
    id: 3,
  },
  NATIVE_HAWAIIAN_OR_PACIFIC_ISLANDER: {
    fullText: "Native Hawaiian or Pacific Islander",
    id: 4,
  },
  WHITE: {
    fullText: "White",
    id: 5,
  },
  OTHER: {
    fullText: "Other",
    id: 6,
  },
  DECLINE_TO_ANSWER: {
    fullText: "Decline to Answer",
    id: 7,
  },
};

const maritalStatusToAnswer = {
  MARRIED: {
    fullText: "Married",
    id: 0,
  },
  DIVORCED: {
    fullText: "Divorced",
    id: 1,
  },
  SINGLE: {
    fullText: "Single",
    id: 2,
  },
  OTHER: {
    fullText: "Other",
    id: 3,
  },
};

const sexToAnswer = {
  MALE: {
    fullText: "Male",
    id: 0,
  },
  FEMALE: {
    fullText: "Female",
    id: 1,
  },
  OTHER: {
    fullText: "Other",
    id: 2,
  },
};

// nick TODO: update this to handle clinical provider options endpoint
export const updateRetrievableResponsesWithPC = async (
  consumer: Consumer,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  retrievableIntakeResponses: any[],
  user: User,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  allQuestions: any[],
  orgId: number,
  prefillInfo: Map<number, IntakeFormSavedData>,
  appointmentId: string,
  appointmentCreatedAt: string,
  intakeFormsData: IntakeFormData[],
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  superscriptQuestions: any[],
) => {
  const hasStartedIntake = Object.keys(prefillInfo).length > 0;

  const consumerUserFieldToQuestionMap = new Map<
    keyof Consumer | keyof User | string,
    number
  >();
  const questionIdToConsumerUserFieldMap = new Map<
    number,
    keyof Consumer | keyof User | string
  >();

  for (const question of superscriptQuestions) {
    // Skip if no questions found
    if (question.intakeQuestions.length === 0) {
      // no questions found for superscript question
      continue;
    }

    // Log warning if multiple questions found
    if (question.intakeQuestions.length > 1) {
      console.warn(
        "multiple questions found for superscript question",
        question.id,
        question,
      );
    }

    const orgQuestionId = question.intakeQuestions[0].id;
    const ssQuestionId = question.id;
    const consumerField =
      superscriptquestionIdToConsumerUserFieldMap.get(ssQuestionId);

    // Only add to maps if consumer field exists
    if (consumerField) {
      consumerUserFieldToQuestionMap.set(consumerField, orgQuestionId);
      questionIdToConsumerUserFieldMap.set(orgQuestionId, consumerField);
    }
  }

  const isConsumerOrUserFieldQuestion = (questionId: number) => {
    return Array.from(consumerUserFieldToQuestionMap.values()).includes(
      questionId,
    );
  };

  // Filter questions to only include those that are either retrievable responses
  // or consumer/user fields, then map them to the updated format
  const updatedRetrievableIntakeResponses = allQuestions
    .map((question) => {
      const retrievedResponse = retrievableIntakeResponses.find(
        (response) => response.intakeQuestionId === question.intakeQuestion.id,
      );

      const questionId = question.intakeQuestion.id;

      // Only process questions that are either consumer/user fields or have a response

      if (isConsumerOrUserFieldQuestion(questionId) || retrievedResponse) {
        /* this ugly logic is to handle the fact that the emergency contact and pcp questions
        ask just for name, but in pc we store first and last name separately
        */

        const getPcValue = (questionId: number) => {
          if (
            questionIdToConsumerUserFieldMap.get(questionId) ===
              "emergencyContactFirstName" ||
            questionIdToConsumerUserFieldMap.get(questionId) ===
              "emergencyContactLastName"
          ) {
            return `${
              consumer.emergencyContactFirstName
                ? consumer.emergencyContactFirstName
                : ""
            } ${
              consumer.emergencyContactLastName
                ? consumer.emergencyContactLastName
                : ""
            }`.trim();
          }
          if (
            questionIdToConsumerUserFieldMap.get(questionId) ===
              "pcpFirstName" ||
            questionIdToConsumerUserFieldMap.get(questionId) === "pcpLastName"
          ) {
            return `${consumer.pcpFirstName ? consumer.pcpFirstName : ""} ${
              consumer.pcpLastName ? consumer.pcpLastName : ""
            }`.trim();
          }
          if (
            questionIdToConsumerUserFieldMap.get(questionId) ===
            "clinicalPharmacySearch"
          ) {
            if (
              !consumer.pharmacyName &&
              !consumer.pharmacyPhone &&
              !consumer.pharmacyEhrId
            ) {
              return null;
            }
            const clinicalPharmacyObject = {
              name: consumer.pharmacyName,
              phone: consumer.pharmacyPhone,
              ehrId: consumer.pharmacyEhrId,
              address: {
                streetAddress: consumer.pharmacyAddress?.streetAddress,
                city: consumer.pharmacyAddress?.city,
                state: consumer.pharmacyAddress?.state,
                zip: consumer.pharmacyAddress?.zip,
              },
            } as ClinicalProviderFormData;
            return clinicalPharmacyObject;
          }
          if (
            questionIdToConsumerUserFieldMap.get(questionId) ===
            "clinicalPcpSearch"
          ) {
            if (
              !consumer.pcpFirstName &&
              !consumer.pcpLastName &&
              !consumer.pcpPhone &&
              !consumer.pcpEhrId
            ) {
              return null;
            }
            const clinicalPcpObject = {
              name: `${consumer.pcpFirstName ? consumer.pcpFirstName : ""} ${
                consumer.pcpLastName ? consumer.pcpLastName : ""
              }`.trim(),
              firstName: consumer.pcpFirstName,
              lastName: consumer.pcpLastName,
              phone: consumer.pcpPhone,
              ehrId: consumer.pcpEhrId,
              address: {
                streetAddress: consumer.pcpAddress?.streetAddress,
                city: consumer.pcpAddress?.city,
                state: consumer.pcpAddress?.state,
                zip: consumer.pcpAddress?.zip,
              },
            } as ClinicalProviderFormData;
            return clinicalPcpObject;
          }
          return consumer[
            questionIdToConsumerUserFieldMap.get(questionId) as string
          ];
        };

        const pcValue = getPcValue(questionId);

        const pcField = questionIdToConsumerUserFieldMap.get(
          questionId,
        ) as string;

        const isEmptyPcValue =
          !pcValue || (Array.isArray(pcValue) && pcValue.length === 0);

        // if the question is a consumer/user field and the pc value is empty and there is no retrieved response, don't include it in r&c
        const noResponseOrPcValue =
          isConsumerOrUserFieldQuestion(questionId) &&
          isEmptyPcValue &&
          !retrievedResponse;

        // if we've answered the question already but the form isn't done, also don't include it bc that would be weird to
        // see it "appear" on your r&c if you leave and come back to intake - we want the question to appear as it did the first time
        // this might be the issue though
        const lastResponseIsIncomplete = retrievedResponse && hasStartedIntake; // this means the retrieved response is from the last in progress intake

        const alwaysSkipReviewAndConfirm = []; // mainly for demographics so we don't ask it in r&c, not implementing now

        const updatedResponse = {
          ...retrievedResponse,
          intakeQuestionId: question.intakeQuestion.id,
          previousResponse: retrievedResponse?.response,
          intakeQuestion: question.intakeQuestion,
          responseCopy: pcValue
            ? formatResponseForCopy(question.intakeQuestion, pcValue)
            : formatResponseForCopy(
                question.intakeQuestion,
                retrievedResponse?.response,
              ),
          isFromPc: pcValue !== "",
        };

        if (pcValue) {
          if (question.intakeQuestion.type === "TEXT") {
            updatedResponse.response =
              pcValue !== "" ? pcValue : retrievedResponse?.response;
          }

          if (question.intakeQuestion.type === "SINGLE_SELECT") {
            let lookupObject: Record<
              string,
              { fullText: string; id: number }
            > | null;
            switch (pcField) {
              case "primaryLanguage":
                lookupObject = languageCodeToAnswer;
                break;
              case "ethnicity":
                lookupObject = ethnicityToAnswer;
                break;
              case "maritalStatus":
                lookupObject = maritalStatusToAnswer;
                break;
              case "sex":
                lookupObject = sexToAnswer;
                break;
              default:
                lookupObject = null;
            }

            if (lookupObject) {
              updatedResponse.response =
                lookupObject[
                  pcValue as keyof typeof lookupObject
                ]?.id.toString() ??
                lookupObject[
                  pcValue.toString().toUpperCase() as keyof typeof lookupObject
                ]?.id.toString();

              updatedResponse.responseCopy =
                lookupObject[pcValue as keyof typeof lookupObject]?.fullText ??
                lookupObject[
                  pcValue.toString().toUpperCase() as keyof typeof lookupObject
                ]?.fullText ??
                "Unknown";
            }
          } else if (question.intakeQuestion.type === "MULTI_SELECT") {
            if (pcField === "race") {
              const pcValueArray = pcValue as string[];
              const formattedAsResponseArray = pcValueArray
                .map(
                  (race) =>
                    `${
                      raceToAnswer[race as keyof typeof raceToAnswer]?.fullText
                    }`,
                )
                .join(", ");
              const updatedResponseArray = pcValueArray.map((race) =>
                raceToAnswer[race as keyof typeof raceToAnswer]?.id.toString(),
              );
              updatedResponse.response = updatedResponseArray;
              updatedResponse.responseCopy = formattedAsResponseArray;
            }
          } else if (question.intakeQuestion.type === "CLINICAL_PROVIDER") {
            updatedResponse.response = pcValue;
          }
        }

        if (noResponseOrPcValue || lastResponseIsIncomplete) {
          if (lastResponseIsIncomplete) {
            const currentPrefillForForm = prefillInfo.get(question.formId);
            if (currentPrefillForForm) {
              // get most recent in-progress response and set it as prefill info
              prefillInfo.set(question.formId, {
                ...currentPrefillForForm,
                responses: {
                  ...currentPrefillForForm.responses,
                  [question.intakeQuestion.id]: {
                    questionNumber: question.intakeQuestion.questionNumber,
                    questionText: question.intakeQuestion.questionText,
                    type: question.intakeQuestion.type,
                    response: updatedResponse.response,
                    richResponse: updatedResponse.richResponse,
                  },
                },
              });
            } else {
              // if no response or pc value, we need to set prefill info
              // this would mean we have empty prefill info
              prefillInfo.set(question.formId, {
                consumerId: consumer.id,
                createdAt: appointmentCreatedAt,
                appointmentId: appointmentId,
                intakeFormId: question.formId,
                intakeFormName:
                  intakeFormsData.find((f) => f.id === question.formId)?.name ??
                  "",
                isComplete: false,
                responses: {
                  [question.intakeQuestion.id]: {
                    questionNumber: question.intakeQuestion.questionNumber,
                    questionText: question.intakeQuestion.questionText,
                    type: question.intakeQuestion.type,
                    response: updatedResponse.response,
                    richResponse: updatedResponse.richResponse,
                  },
                },
              });
            }
          }

          return null;
        }
        return updatedResponse;
      }

      // Return null for questions that don't meet our criteria
      return null;
    })
    // Filter out null values from questions that didn't meet our criteria
    .filter((response) => response !== null);
  // todo: check this filter out nulls stuff

  return {
    pcUpdatedRetrievableIntakeResponses: updatedRetrievableIntakeResponses,
    updatedPrefillInfo: prefillInfo,
    hasStartedIntake: hasStartedIntake,
  };
};

export const formatIntakeFormsBatched = (
  intakeData: IntakeData[],
  retrievableIntakeResponses: Record<number, PolymorphicSavedResponse>,
  prefillInfo: Map<number, IntakeFormSavedData>, // updated with pc values
) => {
  // Initialize empty arrays and objects to collect data from all forms
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  let allIntakeFormQuestions: any[] = [];
  let allPrefillInfo: Record<number, PolymorphicSavedResponse> = {};

  // Process each intake data entry to collect all questions and responses
  for (const data of intakeData) {
    const {
      form: { intakeFormToQuestions },
      response,
    } = data ?? {
      form: {
        intakeFormToQuestions: [] as FormToQuestion[],
      },
      response: undefined,
    };

    // Add questions from this form to our collection
    const formQuestions = intakeFormToQuestions.map((x) => ({
      ...x,
      ...x.intakeQuestion,
    }));

    allIntakeFormQuestions = [...allIntakeFormQuestions, ...formQuestions];

    // Add responses from this form to our collection
    const formPrefillInfo = (response?.responses ?? {}) as Record<
      number,
      PolymorphicSavedResponse
    >;

    allPrefillInfo = {
      ...allPrefillInfo,
      ...formPrefillInfo,
      ...prefillInfo.get(data.form.id)?.responses,
    };
  }

  //need to convert retrievableIntakeResponses to a record keyed by intakeQuestionId or intakeQuestion.id
  const retrievableIntakeResponsesMap = Object.fromEntries(
    Object.entries(retrievableIntakeResponses).map(([key, value]) => {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const intakeQuestion = (value as any).intakeQuestion;
      return [
        intakeQuestion.id,
        {
          questionText: intakeQuestion.questionText,
          response: value.response,
          richResponse: value.richResponse,
        },
      ];
    }),
  );

  // Create combined previous responses
  const previousResponses = {
    ...retrievableIntakeResponsesMap,
    ...allPrefillInfo,
  };

  // Process unlocked questions across all forms
  const unlockedQuestions = new Set(
    allIntakeFormQuestions
      .filter((q) => q.type === "SINGLE_SELECT" || q.type === "MULTI_SELECT")
      .reduce(
        (accum, q) =>
          accum.concat(
            // biome-ignore lint/style/noNonNullAssertion: <explanation>
            q.questionAttributes
              .choices!.filter((c) =>
                responseIsPrefilled(q, c, previousResponses),
              )
              .flatMap((c) => c.conditionalQuestions),
          ),
        [] as number[],
      ),
  );

  // Find hidden questions across all forms
  const tempHiddenQuestions = new Set(
    allIntakeFormQuestions
      .filter(
        (q) =>
          q.questionAttributes.hiddenByDefault && !unlockedQuestions.has(q.id),
      )
      .map((q) => q.id),
  );

  // Process default values for all forms
  const defaultValues = allIntakeFormQuestions.reduce((accum, q) => {
    if (q.id in previousResponses && !tempHiddenQuestions.has(q.id)) {
      accum[q.id] = formatPreviousResponse(
        previousResponses[q.id],
        q.type,
        q.id,
      );
    } else if (q.type === "MULTI_SELECT") {
      accum[q.id] = []; // initialize as empty array instead of undefined
    }
    return accum;
  }, {});

  // Process questions with updated timestamps
  const questionsWithUpdatedAt = allIntakeFormQuestions.map((q) => ({
    ...q,
    updatedAt:
      q.id in defaultValues && defaultValues[q.id]
        ? defaultValues[q.id].updatedAt && !(q.id in allPrefillInfo)
          ? parseISO(defaultValues[q.id].updatedAt)
          : undefined
        : undefined,
  }));

  // Group questions by section
  const groupedQuestions = chunkArray(
    questionsWithUpdatedAt,
    (lastItem, newItem) => lastItem.section === newItem.section,
  );

  // Create sectioned questions
  const sectionedQuestions =
    allIntakeFormQuestions.length === 0
      ? []
      : groupedQuestions.map((group) => ({
          section: group[0].section,
          questions: group,
        }));

  return {
    intakeFormQuestions: allIntakeFormQuestions,
    tempHiddenQuestions,
    defaultValues,
    sectionedQuestions,
  };
};

export const isFormComplete = (formId: number, intakeData: IntakeData[]) => {
  return !!intakeData.find((form) => form.form.id === formId)?.isComplete;
};

// only checks prefill info, not current form values
export const getStartAtQuestionIndex = (
  initialFormSectionsToComplete: TypeformIntakeFormSection[],
  prefillInfo: Map<number, IntakeFormSavedData>,
  hiddenQuestions: Set<number>,
) => {
  // need to update this to handle signature questions

  const startAtQuestionIndex = initialFormSectionsToComplete.findIndex(
    (s) =>
      !isSectionComplete(
        s.sectionId,
        prefillInfo,
        hiddenQuestions,
        initialFormSectionsToComplete,
      ),
  );
  return startAtQuestionIndex === -1 ? 0 : startAtQuestionIndex;
};

const isSectionComplete = (
  sectionId: string,
  prefillInfo: Map<number, IntakeFormSavedData>,
  hiddenQuestions: Set<number>,
  initialFormSectionsToComplete: TypeformIntakeFormSection[],
) => {
  const section = initialFormSectionsToComplete.find(
    (s) => s.sectionId === sectionId,
  );
  if (!section) {
    return false;
  }
  // need to check on hidden questions here as well
  if (prefillInfo.has(section?.formId)) {
    const responses = prefillInfo.get(section?.formId)?.responses;
    if (responses) {
      const questionsToCheck = section.questions.filter(
        (q) => !hiddenQuestions.has(q.intakeQuestion.id),
      );
      return questionsToCheck.every((q) => responses[q.intakeQuestion.id]);
    }
  }
  return false;
};

export const compressImage = async (file: File) => {
  try {
    const compressedFile = await imageCompression(file, {
      maxSizeMB: 1, //must be less than the bodySizeLimit in next.config.js
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    });
    return compressedFile;
  } catch (error) {
    datadogLogs.logger.warn(`Failed to compress image: ${file.name}, ${error}`);
  }
  return null;
};

export const fetchIntakeFormPdf = async (
  formValues: DynamicFormType,
  formId: number,
  // biome-ignore lint/suspicious/noExplicitAny: need to resolve consumer types
  consumer: any,
  formName: string,
  appointmentId: string,
  userLocale: string,
  // biome-ignore lint/suspicious/noExplicitAny: need to resolve allQuestions types
  allQuestions: any[],
  visitData?: {
    location?: {
      address?: string;
      locationName?: string;
    };
  },
  logo?: { url: string; altText: string },
): Promise<Blob> => {
  try {
    const response = await fetch("/api/generate-intake-pdf", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        formValues,
        formId,
        consumer,
        formName,
        appointmentId,
        userLocale,
        allQuestions,
        visitData,
        logo,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate PDF: ${response.statusText}`);
    }

    const pdfBlob = await response.blob();
    return pdfBlob;
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

// function to call the submit-intake-pdf API
export const saveIntakePdfToEhr = async (
  formValues: DynamicFormType,
  formId: number,
  // biome-ignore lint/suspicious/noExplicitAny: need to resolve consumer types
  consumer: any,
  formName: string,
  appointmentId: string,
  userLocale: string,
  // biome-ignore lint/suspicious/noExplicitAny: need to resolve allQuestions types
  allQuestions: any[],
  orgId: number,
  userId: string,
  practiceId: number | undefined,
  visitData?: {
    location?: {
      address?: string;
      locationName?: string;
    };
  },
  logo?: { url: string; altText: string },
): Promise<Response> => {
  try {
    const body = {
      formValues,
      formId,
      consumer,
      formName,
      appointmentId,
      userLocale,
      allQuestions,
      visitData,
      logo,
      orgId,
      userId,
      practiceId,
    };
    const response = await fetch("/api/submit-intake-pdf", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // HTTP/2 leaves statusText empty, so include status + body to keep
      // the root cause visible in Datadog RUM.
      const errorBody = await response.text().catch(() => "");
      throw new Error(
        `Failed to submit PDF for ${formName} to EHR for appointment ${appointmentId} (HTTP ${response.status}): ${errorBody}`,
      );
    }

    return response;
  } catch (error) {
    console.error(
      `Error submitting PDF for ${formName} to EHR for appointment ${appointmentId}:`,
      error,
    );
    throw error;
  }
};

export type DynamicFormType = {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  [x: string]: any;
};

export type TTranslations = ReturnType<typeof useTranslations>;

// this one cannot be used on server side bc of datadog
export const createAndWriteIntakePdf = async (
  pdfBlob: Blob,
  formName: string,
  appointmentId: string,
  orgId: number,
  formId: number,
  consumer: {
    firstname: string;
    lastname: string;
    dob: string;
    id?: string | undefined;
    userId?: string | null | undefined;
    stripeCustomerId?: string | null | undefined;
    primaryInsuranceId?: string | null | undefined;
    ehrPatientId?: string | null | undefined;
    ehrEnterpriseId?: string | null | undefined;
  },
  userId: string,
  practiceId: number | undefined,
  resubmit = false,
) => {
  const formData = new FormData();
  formData.append("file", pdfBlob, `${formName}.pdf`);
  formData.append("name", formName);
  formData.append("fileName", formName);
  formData.append("appointmentId", appointmentId);
  formData.append("orgId", orgId.toString());
  formData.append("formId", formId.toString());
  const patientId = consumer.ehrPatientId ?? consumer.ehrEnterpriseId;
  if (patientId) {
    formData.append("ehrPatientId", patientId);
  }
  if (practiceId) {
    formData.append("practiceId", practiceId.toString());
  }

  const ehrResult = await tryCatchRethrow(
    writeIntakeFormToEhr(formData, userId),
    `writeIntakeFormToEhr, formId: ${formId}, consumerId: ${consumer.id}`,
  );

  // nick: this Array.isArray check is a hack to handle the case where the ehrResult is an array
  // which we return when there is an error with the pc api call
  // not sure why but this is how it is in actions.ts and i don't want to break anything by changing it
  // TODO: fix this post prod push

  if (
    (ehrResult && "error" in ehrResult) ||
    (Array.isArray(ehrResult) && ehrResult.length > 0)
  ) {
    const errorMessage =
      ehrResult && "error" in ehrResult
        ? ehrResult.error?.message
        : "Unknown error";
    datadogLogs.logger.error(
      `Error ${
        resubmit ? "resubmitting" : "saving"
      } ${formName} pdf to ehr for appointment '${appointmentId}': ${errorMessage}`,
      {
        error: errorMessage,
        appointmentId,
        formId,
        orgId,
        formName,
        consumerId: consumer.id,
      },
    );
    throw new Error(errorMessage);
  }
  datadogLogs.logger.info(
    `${formName} pdf successfully saved to ehr for appointment '${appointmentId}'`,
    {
      appointmentId,
      formId,
      orgId,
      formName,
      consumerId: consumer.id,
    },
  );
  return ehrResult as ResponseDto;
};
